import { useCallback, useMemo, useRef, useState } from "react";
import {
  AccountData,
  DMAutomationPlatform,
  DMAutomationSubmission,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import CreatorModePanelWrapper from "../../components/CreatorDashboard/CreatorModePanelWrapper";
import { useDMAutomationSelector } from "../../hooks/useParametricSelector";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import LoadingScreen from "../LoadingScreen";
import { Icon } from "@iconify/react";
import { useTheme } from "../../hooks/useTheme";
import { useLiveUpdatingDMAutomation } from "../../hooks/useLiveHooks/useLiveUpdatingDMAutomation";
import DMAutomationFlowPanel from "../../components/CreatorDashboard/AutoDM/Dashboard/DMAutomationFlowPanel";
import DMAutomationSettingsPanel from "../../components/CreatorDashboard/AutoDM/Dashboard/DMAutomationSettingsPanel";
import DMAutomationRenameModal from "../../components/CreatorDashboard/AutoDM/Items/DMAutomationRenameModal";
import FatHeaderActionButton from "../../components/Buttons/FatHeaderActionButton";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import { VerticalDivider } from "../../components/Dividers/VerticalDivider";
import { GetDateWithTime } from "@markit/common.utils";
import { getCountFromServer } from "../../firebase";
import { getUserDmAutomationSubmissionsRef } from "../../utils/FirebaseUtils";
import CreateDMAutomationWrapper from "../../components/CreatorDashboard/AutoDM/Dashboard/CreateDMAutomationWrapper";
import DMAutomationDashboardAnalytics from "../../components/CreatorDashboard/AutoDM/Dashboard/DMAutomationDashboardAnalytics";
import { useLiveUpdatingCampaign } from "../../hooks/useLiveHooks/useLiveUpdatingCampaign";
import { AutomationItemPreview } from "../../components/DisplayItem/CampaignItem/AutomationItemPreview";
import StandardListContainerBox from "../../components/Containers/StandardListContainerBox";
import SearchBoxContactsContainer, {
  SearchBoxContactsMode,
} from "../../components/Containers/SearchBox/SearchBoxContactsContainer";
import GridTableHeader from "../../components/Headers/GridTableHeader";
import { Virtuoso } from "react-virtuoso";
import { EmptyStateFlatlist } from "../../components/EmptyStates/EmptyStateFlatlist";
import {
  searchByPhoneNumber,
  searchByPhoneNumberNoResults,
} from "../../utils/phoneUtils";
import EmptyStateButton from "../../components/Buttons/EmptyStateButton";
import { useLoadDMAutomationSubmissions } from "../../hooks/useLoadDMAutomationSubmissions";
import { CircularProgress } from "@mui/material";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import ProfileItem from "../../components/ProfileItem";
import { formatPhoneNumber } from "../../utils/FormatPhoneNumber";
import FullProfilePreviewModal from "../../components/FollowerProfile/FullProfilePreviewModal";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../../components/DropdownMenu";
import SocialProfilePicsLabel from "../../components/DisplayItem/AutoDM/SocialProfilePicsLabel";

const DMAUTOMATION_ITEM_FLEX_SECTIONS = [7, 3];
const DMAUTOMATION_ITEM_FLEX_SECTIONS_WITH_IG = [5, 2, 3];

enum DMAutomationDashboardTabs {
  DASHBOARD = 0,
  ANALYTICS = 1,
  SETTINGS = 2,
}

// Thin wrapper that remounts the dashboard whenever the automation id changes
const DMAutomationDashboard = () => {
  const dmAutomationId = useDMAutomationSelector();
  return <DMAutomationDashboardContent key={dmAutomationId} />;
};

const DMAutomationDashboardContent = () => {
  const { accountData, followingAccountData } =
    useSelector(getAccountState).account;
  const { theme } = useTheme();
  const dmAutomationId = useDMAutomationSelector();
  const [tabValue, setTabValue] = useState(DMAutomationDashboardTabs.DASHBOARD);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [editingDMAutomation, setEditingDMAutomation] = useState(false);
  const [editContent, setEditContent] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMode, setSearchMode] = useState(SearchBoxContactsMode.NAME);
  const [searchedPhoneUser, setSearchedPhoneUser] = useState<
    AccountData | undefined
  >(undefined);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [profilePreviewSelected, setProfilePreviewSelected] = useState<
    AccountData | undefined
  >(undefined);

  const styles = {
    containerView: {
      padding: "12px 14px",
    },
    sectionContainer: {
      backgroundColor: Colors.WHITE,
      padding: 14,
      borderRadius: 14,
      flex: 1,
    },
    headerTitle: {
      color: Colors.BLACK,
      fontSize: 14,
      fontWeight: 500,
    },
  };

  const {
    liveDMAutomation,
    liveDMAutomationTriggers,
    liveDMAutomationContents,
    isLiveDMAutomationError,
    loaded,
  } = useLiveUpdatingDMAutomation({
    initialDMAutomationId: dmAutomationId,
    userId: accountData.uid,
  });

  const {
    liveCampaign,
    liveCampaignDetail,
    loaded: campaignLoaded,
  } = useLiveUpdatingCampaign({
    initialCampaignId: liveDMAutomation?.campaignId || "",
    userId: accountData.uid,
  });

  const automationId = liveDMAutomation?.id;
  // Facebook (Messenger) followers have no username, so the column is Instagram only
  const showIgUsername =
    liveDMAutomation?.platform === DMAutomationPlatform.INSTAGRAM;
  const flexSections = showIgUsername
    ? DMAUTOMATION_ITEM_FLEX_SECTIONS_WITH_IG
    : DMAUTOMATION_ITEM_FLEX_SECTIONS;
  const hasCampaign = !!liveDMAutomation?.campaignId;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!automationId || !hasCampaign) {
      return;
    }
    const snap = await getCountFromServer(
      getUserDmAutomationSubmissionsRef(accountData.uid, automationId)
    );
    setSubmissionsCount(snap.data().count);
  }, [accountData.uid, automationId, hasCampaign]);

  const {
    fetchedSubmissions,
    fetchedUserData,
    fetchedFollowerData,
    isLoading: isSubmissionsLoading,
    isFinished: isSubmissionsFinished,
    loadSubmissions,
    loadMoreSubmissions,
    loadSearchResultsThrottled,
    loadSubmissionsForUid,
  } = useLoadDMAutomationSubmissions({
    creatorUid: accountData.uid,
    dmAutomationId,
    windowSize: 15,
  });

  const submissionsLoadedRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (hasCampaign && automationId && !submissionsLoadedRef.current) {
      submissionsLoadedRef.current = true;
      await loadSubmissions();
    }
  }, [hasCampaign, automationId, loadSubmissions]);

  const userByUid = useMemo(() => {
    const map = new Map<string, AccountData>();
    fetchedUserData.forEach((user) => map.set(user.uid, user));
    return map;
  }, [fetchedUserData]);

  const igUsernameByUid = useMemo(() => {
    const map = new Map<string, string>();
    fetchedFollowerData.forEach((follower) => {
      if (follower.igUsername) {
        map.set(follower.uid, follower.igUsername);
      }
    });
    return map;
  }, [fetchedFollowerData]);

  const visibleSubmissions = useMemo(() => {
    if (searchTerm.trim() === "") {
      return fetchedSubmissions;
    }
    if (searchMode === SearchBoxContactsMode.PHONE) {
      if (!searchedPhoneUser) {
        return [];
      }
      return fetchedSubmissions.filter((s) => s.uid === searchedPhoneUser.uid);
    }
    return fetchedSubmissions.filter((s) =>
      userByUid.get(s.uid)?.fullName.toLowerCase().includes(searchTerm)
    );
  }, [
    fetchedSubmissions,
    searchMode,
    searchTerm,
    searchedPhoneUser,
    userByUid,
  ]);

  const handleSearch = useCallback(
    async (text: string) => {
      setSearchedPhoneUser(undefined);
      const lower = text.toLowerCase();
      setSearchTerm(lower);
      if (searchMode === SearchBoxContactsMode.NAME) {
        if (text !== "") {
          loadSearchResultsThrottled(lower);
        }
      } else if (text !== "") {
        const result = await searchByPhoneNumber(
          text,
          accountData.uid,
          followingAccountData
        );
        if (result) {
          setSearchedPhoneUser(result.user);
          loadSubmissionsForUid(result.user.uid);
        }
      }
    },
    [
      accountData.uid,
      followingAccountData,
      loadSearchResultsThrottled,
      loadSubmissionsForUid,
      searchMode,
    ]
  );

  // Opens the edit flow. Pass a target to shortcut straight into that nested
  // editor (from the flow preview's edit buttons); omit it to land on the
  // Automation summary hub (the header "Edit" button).
  const openDMAutomationEditor = useCallback((editContent: boolean) => {
    setEditContent(editContent);
    setEditingDMAutomation(true);
  }, []);

  const renderSubmissionItem = useCallback(
    (submission: DMAutomationSubmission) => {
      const user = userByUid.get(submission.uid);
      const menuItems: DropdownMenuItem[] = [
        {
          title: "Message",
          key: "message",
          onPress: () => setProfilePreviewSelected(user),
        },
      ];
      return (
        <>
          <div className="AlignedRow" style={{ paddingBlock: 10 }}>
            <div className="AlignedRow" style={{ flex: flexSections[0] }}>
              <div style={{ marginLeft: 14, width: "100%" }}>
                <ProfileItem
                  profileItem={user}
                  subtext={
                    <span className="smallBodySubtext">
                      {formatPhoneNumber(user?.phoneNumber ?? "")}
                    </span>
                  }
                  onItemPress={() => setProfilePreviewSelected(user)}
                />
              </div>
            </div>
            {showIgUsername ? (
              <div className="AlignedRow" style={{ flex: flexSections[1] }}>
                {igUsernameByUid.has(submission.uid) ? (
                  <a
                    className="bodySubtext"
                    href={`https://www.instagram.com/${encodeURIComponent(
                      igUsernameByUid.get(submission.uid) ?? ""
                    )}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginLeft: 14,
                      color: Colors.BLUE5,
                      textDecoration: "underline",
                    }}
                  >
                    @{igUsernameByUid.get(submission.uid)}
                  </a>
                ) : null}
              </div>
            ) : null}
            <div
              className="AlignedRowSpaced"
              style={{ flex: flexSections[flexSections.length - 1] }}
            >
              <span className="bodySubtext" style={{ marginLeft: 14 }}>
                {GetDateWithTime(submission.createdAt, true)}
              </span>
              <DropdownMenu
                dropdownItems={menuItems}
                trigger={["click"]}
                placement="bottomLeft"
                altWidth={120}
              >
                <Icon
                  icon="ion:ellipsis-horizontal"
                  height={20}
                  style={{ color: Colors.BLACK, marginRight: 20 }}
                />
              </DropdownMenu>
            </div>
          </div>
          <HorizontalDivider />
        </>
      );
    },
    [flexSections, igUsernameByUid, showIgUsername, userByUid]
  );

  const renderSubmissionsFooter = useCallback(
    () => (
      <>
        {isSubmissionsLoading && !isSubmissionsFinished ? (
          <div className="Centering" style={{ paddingBlock: 14 }}>
            <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
          </div>
        ) : null}
        <div style={{ paddingBottom: 20 }} />
      </>
    ),
    [isSubmissionsLoading, isSubmissionsFinished]
  );

  const renderSubmissionsEmptyState = useCallback(
    () => (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isSubmissionsLoading}
        containerStyles={{ paddingTop: 120 }}
        noResultsDescription={
          searchMode === SearchBoxContactsMode.PHONE
            ? searchByPhoneNumberNoResults(searchTerm)
            : "Check for typos, search for something else, or switch to phone search."
        }
        nonSearchEmptyView={
          <EmptyStateButton
            title={"No Users Sent Flow"}
            description={
              "Users that complete the automation flow will show up here"
            }
            icon={
              <Icon
                icon="ion:share"
                height={70}
                style={{ color: Colors.GRAY1 }}
              />
            }
            iconBox={109.5}
            containerStyles={{ paddingTop: 120 }}
          />
        }
      />
    ),
    [isSubmissionsLoading, searchMode, searchTerm]
  );

  const listComp = (
    <Virtuoso
      style={{ height: "100%" }}
      data={visibleSubmissions}
      endReached={() => {
        if (searchTerm.trim() === "") {
          loadMoreSubmissions();
        }
      }}
      increaseViewportBy={200}
      itemContent={(_index, submission) => renderSubmissionItem(submission)}
      components={{
        Footer: renderSubmissionsFooter,
        EmptyPlaceholder: renderSubmissionsEmptyState,
      }}
    />
  );

  if (
    !loaded ||
    !liveDMAutomation ||
    !liveDMAutomationTriggers ||
    !liveDMAutomationContents ||
    (liveDMAutomation.campaignId && !campaignLoaded)
  ) {
    return (
      <LoadingScreen
        isLoadingPage={!loaded || !campaignLoaded}
        error={isLiveDMAutomationError}
      />
    );
  }

  return (
    <CreatorModeWrapper>
      <CreatorModePanelWrapper
        title={
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <div className="AlignedRow" style={{ gap: 7, paddingBottom: 20 }}>
              <span>{liveDMAutomation.title}</span>
              <div>
                <Icon
                  icon={"ion:create"}
                  height={22}
                  style={{ ...theme.LabelText, cursor: "pointer" }}
                  onClick={() => setShowRenameModal(true)}
                />
              </div>
            </div>
            <StandardBorderedContainer
              containerStyles={{ alignSelf: "flex-start" }}
            >
              <div className="AlignedRow">
                <div
                  className="AlignedRow"
                  style={{ ...styles.containerView, gap: 7, maxWidth: 140 }}
                >
                  <SocialProfilePicsLabel
                    igAccountIds={liveDMAutomation.igAccountIds}
                    platform={liveDMAutomation.platform}
                    textStyles={styles.headerTitle}
                  />
                </div>
                <VerticalDivider height={41} />
                <div className="AlignedRow" style={{ ...styles.containerView }}>
                  <span className="bodyMedium">
                    Last Updated:{" "}
                    {GetDateWithTime(liveDMAutomation.updatedAt, true)}
                  </span>
                </div>
                <VerticalDivider height={41} />
                {liveDMAutomation.requireFollow ? (
                  <>
                    <div
                      className="AlignedRow"
                      style={{ ...styles.containerView }}
                    >
                      <span className="bodyMedium">Require Follow</span>
                    </div>
                    <VerticalDivider height={41} />
                  </>
                ) : null}
                <div className="AlignedRow" style={{ ...styles.containerView }}>
                  <span className="bodyMedium">
                    {liveDMAutomation.campaignId
                      ? `Collect Phone${liveDMAutomation.removeCompliance ? " (Compliance Removed)" : ""}`
                      : "DM Only"}
                  </span>
                </div>
              </div>
            </StandardBorderedContainer>
          </div>
        }
        headerContainerStyles={{ alignItems: "center" }}
        headerRight={
          <FatHeaderActionButton
            title="Edit"
            onPress={() => openDMAutomationEditor(false)}
            icon={
              <Icon icon={"ion:create"} height={17} style={{ marginTop: -2 }} />
            }
            containerStyles={{ width: "fit-content" }}
          />
        }
        headerTabs={{
          tabNames: ["Dashboard", "Analytics", "Settings"],
          tabValue: tabValue,
          setTabValue: setTabValue,
        }}
      >
        <div style={{ padding: 30 }}>
          {tabValue === DMAutomationDashboardTabs.DASHBOARD ? (
            liveDMAutomation.campaignId ? (
              <div className="ColumnNormal" style={{ gap: 30 }}>
                {liveCampaign && liveCampaignDetail ? (
                  <StandardBorderedContainer
                    containerStyles={styles.sectionContainer}
                  >
                    <AutomationItemPreview
                      campaignItem={liveCampaign}
                      campaignDetail={liveCampaignDetail}
                      onEventDashboard
                      dmAutomation={liveDMAutomation}
                    />
                  </StandardBorderedContainer>
                ) : null}
                <StandardListContainerBox
                  searchComp={
                    <SearchBoxContactsContainer
                      searchTerm={searchTerm}
                      handleSearch={handleSearch}
                      searchMode={searchMode}
                      setSearchMode={setSearchMode}
                    />
                  }
                  subHeaderComp={
                    <div
                      style={{
                        paddingTop: 6,
                        paddingBottom: 18,
                        paddingInline: 10,
                      }}
                    >
                      <span className="bodyMedium">
                        Sent ({submissionsCount})
                      </span>
                    </div>
                  }
                  gridHeaderComp={
                    <GridTableHeader
                      flexSections={flexSections}
                      sectionTitles={
                        showIgUsername
                          ? ["Name", "Username", "Sent At"]
                          : ["Name", "Sent At"]
                      }
                    />
                  }
                  listComp={listComp}
                  isLoading={false}
                />
              </div>
            ) : (
              <DMAutomationFlowPanel
                dmAutomationTriggers={liveDMAutomationTriggers}
                dmAutomationContents={liveDMAutomationContents}
                platform={liveDMAutomation.platform}
                onEditItem={(isContent) => openDMAutomationEditor(isContent)}
              />
            )
          ) : tabValue === DMAutomationDashboardTabs.ANALYTICS ? (
            <DMAutomationDashboardAnalytics
              automationId={liveDMAutomation.id}
              hasCampaign={!!liveDMAutomation.campaignId}
              triggers={liveDMAutomationTriggers}
              submissionsCount={submissionsCount}
              platform={liveDMAutomation.platform}
            />
          ) : tabValue === DMAutomationDashboardTabs.SETTINGS ? (
            <DMAutomationSettingsPanel dmAutomation={liveDMAutomation} />
          ) : null}
        </div>
      </CreatorModePanelWrapper>
      {editingDMAutomation ? (
        <CreateDMAutomationWrapper
          showModal={editingDMAutomation}
          setShowModal={(show) => {
            setEditingDMAutomation(show);
            if (!show) setEditContent(false);
          }}
          existingDmAutomation={liveDMAutomation}
          existingDmTriggers={liveDMAutomationTriggers}
          existingDmContents={liveDMAutomationContents}
          initialEditContent={editContent}
          initialPlatform={liveDMAutomation.platform}
        />
      ) : null}
      {showRenameModal ? (
        <DMAutomationRenameModal
          dmAutomation={liveDMAutomation}
          showModal={showRenameModal}
          setShowModal={setShowRenameModal}
        />
      ) : null}
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
        />
      ) : null}
    </CreatorModeWrapper>
  );
};

export default DMAutomationDashboard;
